import DOMPurify from 'dompurify'
import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Cancel, CheckCircle } from '@mui/icons-material'
import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material'

import { useLazyGetInternalFeatureExampleQuery } from '../../../../api/categories'
import { FeatureExampleItem, FeatureExamplePosition } from '../../../categories/types/Categories'

const htmlSx = { '& p': { my: 0.5 }, '& img': { maxWidth: '100%' } }

const ExampleItems: FC<{ title: string; icon: JSX.Element; items: FeatureExampleItem[] }> = ({ title, icon, items }) => (
  <Stack spacing={1.5}>
    <Stack direction="row" spacing={1} alignItems="center">
      {icon}
      <Typography fontWeight="bold">{title}</Typography>
    </Stack>
    {items.map((item, index) => (
      <Box key={item.itemId ?? index}>
        <Typography variant="subtitle2" fontWeight="bold">
          {item.title}
        </Typography>
        <Box sx={htmlSx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.desc ?? '') }} />
        {item.imageUrl && <Box component="img" src={item.imageUrl} alt="" loading="lazy" sx={{ maxWidth: '100%', maxHeight: 320, mt: 1 }} />}
      </Box>
    ))}
  </Stack>
)

// Loaded when first shown, at the analysis' store and market, as legacy's "Open".
export const FeatureExample: FC<{ featureId: string; position: FeatureExamplePosition }> = ({ featureId, position }) => {
  const { t } = useTranslation()
  const [loadExample, exampleQuery] = useLazyGetInternalFeatureExampleQuery()

  useEffect(() => {
    loadExample({ featureId, position }, true)
  }, [featureId, loadExample, position])

  if (exampleQuery.isError) return <Typography color="error.main">{t('internal-analysts:analysis_example_failed')}</Typography>
  if (!exampleQuery.isSuccess) return <Skeleton variant="rectangular" height={120} />
  const example = exampleQuery.data
  if (!example) return <Typography color="text.secondary">{t('internal-analysts:analysis_example_none')}</Typography>

  return (
    <Stack spacing={2}>
      <Box>
        <Typography fontWeight="bold">{t('internal-analysts:analysis_example_explanation')}</Typography>
        <Box sx={htmlSx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(example.desc ?? '') }} />
      </Box>
      <Grid container spacing={3}>
        {example.corrects.length > 0 && (
          <Grid item xs={12} md={6}>
            <ExampleItems
              title={t('internal-analysts:analysis_example_means')}
              icon={<CheckCircle color="success" fontSize="small" />}
              items={example.corrects}
            />
          </Grid>
        )}
        {example.wrongs.length > 0 && (
          <Grid item xs={12} md={6}>
            <ExampleItems title={t('internal-analysts:analysis_example_not_means')} icon={<Cancel color="error" fontSize="small" />} items={example.wrongs} />
          </Grid>
        )}
      </Grid>
    </Stack>
  )
}
